<template>
    <div>
        <busy-modal :show="loading" :overlay="true"></busy-modal>
        <b-row>
            <b-col md="12">
                <b-card
                    title="Reservation Status"
                    class="mb-2 w-100 text-center text-light bg-sidebar reservation-status"
                    no-body
                >
                    <template #header>
                        <b-row>
                            <b-col md="12">
                                Reservation Status
                            </b-col>
                        </b-row>
                    </template>
                    <b-card-body>
                        <b-row>
                            <b-col
                                md="12"
                                class="bg-white reservation-state-container"
                                :class="{ 'airbnb-confirmed': isAirBnbConfirmed }"
                                @click="showChangeAirbnbReservationToPaidModal()"
                            >
                                <span :class="getReservationStatusIcon(values.reservationStatus)" />
                                <span class="state-string" :class="getReservationStatusColor(values.reservationStatus)">
                                    {{ values.reservationStatus.state_string }}
                                </span>
                            </b-col>
                        </b-row>
                        <b-row class="balance-container">
                            <b-col md="6" class="align-left">
                                {{ getBalanceLabel(values) }}:
                                <div v-if="balance.isRemaining" class="balance-remaining">
                                    {{ getOtherDepositsPaid(values) }}
                                </div>
                            </b-col>
                            <b-col md="6" class="align-right">
                                <span class="balance-value" :class="balance.balanceClass">{{ balance.balance }}</span>
                            </b-col>
                        </b-row>
                    </b-card-body>

                    <b-card-footer class="reservation-type-container">
                        <b-row>
                            <b-col md="6" class="align-left">
                                <span class="align-left">Reservation Type</span>
                            </b-col>
                            <b-col md="6" class="align-right">
                                <span class="reservation-type">
                                    <span v-if="values.stayType == 'OWNER'">Owner Stay</span>
                                    <span v-else>Guest Stay</span>
                                </span>
                            </b-col>
                        </b-row>
                    </b-card-footer>
                </b-card>
            </b-col>
        </b-row>
        <b-row>
            <b-col md="12">
                <b-card title="" class="text-center confirmation-breakdown sidebar-header-card" no-body>
                    <template #header>
                        <font-awesome-icon :icon="['far', 'square-check']" fixed-width></font-awesome-icon>&nbsp;&nbsp;
                        Confirmation Breakdown
                    </template>
                    <div>
                        <b-card-sub-title class="mb-2"
                            >Derived from
                            <a
                                title="Click to go to Reservation Deposit settings page."
                                href="app/property-manager/reservation-deposit/"
                                >Reservation Deposit Rules</a
                            ></b-card-sub-title
                        >
                    </div>
                    <b-card-body class="confirmation-breakdown-body">
                        <b-card-title
                            >Confirmed = Res. Dep.<span v-if="getShowOtherDeposits(values)">+ Other Dep.</span></b-card-title
                        >
                        <b-card-text>
                            <table class="confirmation-breakdown-body-table">
                                <tbody>
                                    <tr class="bottom-border">
                                        <td class="text-left">
                                            Res. Deposit
                                            <small v-if="!values.confirmationBreakdown.deposit_is_flat"
                                                >({{ values.confirmationBreakdown.reservation_deposit }})</small
                                            >
                                        </td>
                                        <td class="text-right">
                                            {{ values.confirmationBreakdown.reservation_deposit_sum }}
                                        </td>
                                    </tr>
                                    <tr v-for="(item, i) in values.confirmationBreakdown.reservation_deposit_components" :key="item.name + i">
                                        <td class="text-left">
                                            <small>* {{ item.name }}</small>
                                        </td>
                                        <td class="text-right">
                                            <small>{{ item.value }}</small>
                                        </td>
                                    </tr>
                                    <tr v-if="getShowOtherDeposits(values)">
                                        <td class="text-left">Other Deposits</td>
                                        <td class="text-right">
                                            <small>{{ values.confirmationBreakdown.deposit_payment_sum }}</small>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </b-card-text>
                    </b-card-body>
                    <b-card-footer class="confirmation-breakdown-footer">
                        <table class="confirmation-breakdown-footer-table">
                            <tbody>
                                <tr>
                                    <td class="text-left">Total To Confirm</td>
                                    <td class="text-right">{{ values.confirmationBreakdown.reservation_confirm_sum }}</td>
                                </tr>
                                <tr>
                                    <td class="text-left">Paid Sum</td>
                                    <td class="text-right">{{ values.confirmationBreakdown.paid_sum }}</td>
                                </tr>
                                <tr v-if="values.confirmationBreakdown.is_last_minute">
                                    <td class="last-minute" colspan="2">
                                        <small
                                            >Invoice requires FULL payment.</small
                                        >
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </b-card-footer>
                </b-card>
            </b-col>
        </b-row>
        <b-row>
            <b-col md="12">
                <b-card title="" class="text-center guest-details sidebar-header-card">
                    <template #header>
                        <font-awesome-icon :icon="['fas', 'user']" fixed-width></font-awesome-icon>&nbsp;&nbsp; Guest
                        Details
                    </template>
                    <b-card-body>
                        <b-card-text>
                            <div class="guest-name-container">
                                <a class="guest-name" :href="`/app/guest/${values.reservation.user.id}/edit`">{{
                                    values.reservation.user.short_name
                                }}</a>
                            </div>
                            <hr />
                            <table class="guest-details-body-table">
                                <tbody>
                                    <tr>
                                        <td class="text-left guest-details-label">
                                            <font-awesome-icon :icon="['fas', 'envelope']" fixed-width />
                                        </td>
                                        <td class="text-left">
                                            <a :href="`mailto:${values.reservation.user.email}`">{{
                                                values.reservation.user.email
                                            }}</a>
                                        </td>
                                    </tr>
                                    <tr v-if="!isNil(values.reservation.user.phone.link_phone)">
                                        <td class="text-left guest-details-label">
                                            <font-awesome-icon :icon="['fas', 'phone-flip']" fixed-width />
                                        </td>
                                        <td class="text-left">
                                            <a :href="`tel:${values.reservation.user.phone.link_phone}`">{{
                                                values.reservation.user.phone.display_phone
                                            }}</a>
                                        </td>
                                    </tr>
                                    <tr v-if="values.reservation.user.address.address1">
                                        <td class="text-left guest-details-label">
                                            <font-awesome-icon :icon="['fas', 'location-dot']" fixed-width />
                                        </td>
                                        <td class="text-left">{{ values.reservation.user.address.address1 }}</td>
                                    </tr>
                                    <tr v-if="values.reservation.user.address.address2">
                                        <td class="text-left"></td>
                                        <td class="text-left">{{ values.reservation.user.address.address2 }}</td>
                                    </tr>
                                    <tr
                                        v-if="values.reservation.user.address.city || values.reservation.user.address.state"
                                    >
                                        <td class="text-left"></td>
                                        <td class="text-left">
                                            {{ values.reservation.user.address.city }}
                                            <span
                                                v-if="
                                                    values.reservation.user.address.city &&
                                                        values.reservation.user.address.state
                                                "
                                                >,</span
                                            >
                                            {{
                                                values.reservation.user.address.state
                                                    ? values.reservation.user.address.state.code
                                                    : ''
                                            }}
                                        </td>
                                    </tr>
                                    <tr v-if="values.reservation.user.address.zip">
                                        <td class="text-left"></td>
                                        <td class="text-left">{{ values.reservation.user.address.zip }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </b-card-text>
                    </b-card-body>
                    <b-card-footer class="reservation-icons">
                        <reservation-digital-signature-icon
                            size="39"
                            @sign-click="$bvModal.show('digitalSignatureRequestModal')"
                            :reservation="values.reservation"
                            v-if="!values.reservation.is_quote"
                        />
                        <reservation-verification-status-icon size="2x" :status="values.reservation.verification_status"/>
                        <reservation-has-services-icon size="39" v-if="values.reservation.has_services" />
                        <reservation-travel-insurance-icon size="39" v-if="values.reservation.has_travel_insurance" />
                        <reservation-guest-status-icon size="39" :reservation="values.reservation" />
                        <reservation-pets-icon size="39" v-if="values.reservation.has_pets" />
                        <digital-signature-request-modal
                            modalId="digitalSignatureRequestModal"
                            :initialValues="values.reservation"
                            :onUpdate="onModalUpdate"
                        />
                    </b-card-footer>
                </b-card>
            </b-col>
        </b-row>
        <b-row v-if="!isQuote">
            <b-col md="12">
                <b-card title="" class="text-center transactions-window sidebar-header-card">
                    <template #header>
                        <a
                            :href="userVirtualTerminalUrl"
                            class="virtual-terminal-link"
                            target="_blank"
                            v-if="userVirtualTerminalUrl"
                            title="Virtual Terminal"
                            ><font-awesome-icon :icon="['far', 'credit-card']" fixed-width></font-awesome-icon></a
                        >
                        <font-awesome-icon
                            v-if="!userVirtualTerminalUrl"
                            :icon="['far', 'credit-card']"
                            fixed-width
                        ></font-awesome-icon
                        >&nbsp;&nbsp; Transaction Window
                    </template>
                    <b-card-body>
                        <ul class="transactions-window-list">
                            <li v-if="!userHasPayPal">
                                <button
                                    href="javascript:void(0)"
                                    :disabled="processPaymentDisabled"
                                    @click="processPayment()"
                                    :class="{
                                        'green-box-process': !processPaymentDisabled,
                                        'disable-box': processPaymentDisabled,
                                    }"
                                >
                                    <img
                                        v-if="!processPaymentDisabled"
                                        src="/static/images/icons/reservation/payment.svg"
                                        alt="payment"
                                        class="img-fluid"
                                    />
                                    <img
                                        v-if="processPaymentDisabled"
                                        src="/static/images/icons/reservation/payment-blur.svg"
                                        alt="payment"
                                        class="img-fluid"
                                    />
                                    <span>Process<br />Payment</span>
                                </button>
                            </li>
                            <li v-if="userHasPayPal">
                                <button
                                    href="javascript:void(0)"
                                    @click="showSendPayPalInvoiceEmailModal()"
                                    class="blue-box"
                                >
                                    <img
                                        src="/static/images/icons/reservation/paypal.svg"
                                        alt="paypal"
                                        class="img-fluid"
                                    />
                                    <span>PayPal<br />Invoice</span>
                                </button>
                            </li>
                            <li>
                                <button href="javascript:void(0)" @click="recordPayment()" class="green-box">
                                    <img
                                        src="/static/images/icons/reservation/payment.svg"
                                        alt="payment"
                                        class="img-fluid"
                                    />
                                    <span>Record<br />Payment</span>
                                </button>
                            </li>
                            <li v-if="!userHasPayPal">
                                <button
                                    href="javascript:void(0)"
                                    @click="processRefund()"
                                    class="disable-box"
                                    disabled="disabled"
                                >
                                    <img
                                        src="/static/images/icons/reservation/refund-blur.svg"
                                        alt="payment"
                                        class="img-fluid"
                                    />
                                    <span>Process<br />Refund</span>
                                </button>
                            </li>
                            <li>
                                <button href="javascript:void(0)" @click="recordRefund()" class="orange-box">
                                    <img
                                        src="/static/images/icons/reservation/refund.svg"
                                        alt="payment"
                                        class="img-fluid show-img"
                                    />
                                    <img
                                        src="/static/images/icons/reservation/refund-white.svg"
                                        alt="payment"
                                        class="img-fluid hide-img"
                                    />
                                    <span>Record<br />Refund</span>
                                </button>
                            </li>
                            <li v-if="!userHasPayPal">
                                <button
                                    href="javascript:void(0)"
                                    @click="processPreAuth()"
                                    :disabled="processPreAuthDisabled"
                                    :class="{
                                        'yellow-box-process': !processPreAuthDisabled,
                                        'disable-box': processPreAuthDisabled,
                                    }"
                                >
                                    <img
                                        v-if="!processPreAuthDisabled"
                                        src="/static/images/icons/reservation/pre-auth-white.svg"
                                        alt="payment"
                                        class="img-fluid hide-img"
                                    />
                                    <img
                                        v-if="processPreAuthDisabled"
                                        src="/static/images/icons/reservation/pre-auth-blur.svg"
                                        alt="payment"
                                        class="img-fluid"
                                    />
                                    <span>Process<br />PreAuth</span>
                                </button>
                            </li>
                            <li v-if="!userHasPayPal">
                                <button href="javascript:void(0)" @click="recordPreAuth()" class="yellow-box">
                                    <img
                                        src="/static/images/icons/reservation/pre-auth.svg"
                                        alt="payment"
                                        class="img-fluid show-img"
                                    />
                                    <img
                                        src="/static/images/icons/reservation/pre-auth-white.svg"
                                        alt="payment"
                                        class="img-fluid hide-img"
                                    />
                                    <span>Record<br />PreAuth</span>
                                </button>
                            </li>
                        </ul>
                    </b-card-body>
                    <b-card-footer> </b-card-footer>
                </b-card>
            </b-col>
        </b-row>
        <b-row v-if="!isQuote">
            <b-col md="12" class="correspondence-container">
                <b-card title="" class="text-center correspondence-options sidebar-header-card" no-body>
                    <template #header>
                        <a
                            @click="showReportTemplatesModal"
                            title="Manage Documents"
                            :class="{'correspondence-options-link': isCanManageDocumentShortcuts }"><font-awesome-icon :icon="['fas', 'message']" fixed-width></font-awesome-icon></a>&nbsp;&nbsp;
                        Guest Correspondence
                    </template>
                    <b-card-body class="communication-card-body">
                        <b-card-text>
                            <table class="report-templates-table">
                                <tr class="communication-row" v-if="userPermissions.can_view_inbox">
                                    <td class="text-left communication-text">
                                        View All
                                    </td>
                                    <td class="d-flex communication-buttons">
                                        <a title="View All" :href="values.reservation.conversation_url">
                                            <span class="reservation-communication-inbox" />
                                        </a>
                                    </td>
                                </tr>                            
                                <tr v-for="rt in reportTemplatesFiltered" class="communication-row">
                                    <td class="text-left communication-text" @click="showSendEmailModal(rt.id)">
                                        Send {{ rt.report_name }}
                                    </td>
                                    <td class="d-flex communication-buttons">
                                        <a title="Send" @click="showSendEmailModal(rt.id)">
                                            <span class="reservation-communication-send" />
                                        </a>
                                        <a
                                            title="Print"
                                            :href="getReportTemplateInlineURL(rt.id)"
                                            target="_blank"
                                            :class="{'print-report-template-null': rt.id == null}"
                                            :disabled="rt.id == null"
                                            v-show="rt.id != null"
                                        >
                                            <span class="reservation-communication-print" />
                                        </a>
                                    </td>
                                </tr>
                            </table>
                        </b-card-text>
                        <hr class="correspondence-hr" />
                        <b-card-text class="pdf-download-container">
                            <b-dropdown
                                text="PDF Download"
                                split-variant="outline-primary"
                                variant="outline-primary"
                                size="lg"
                                no-flip
                                class="correspondence-dropdown mr-2"
                            >
                                <b-dropdown-item
                                    class="correspondence-dropdown-item"
                                    v-for="rt in reportTemplatesNoPayPal"
                                    :key="`rpt-dnl ${rt.id}`"
                                    :href="getReportTemplateDownloadURL(rt.id)"
                                    >{{ rt.report_name }}</b-dropdown-item
                                >
                            </b-dropdown>               
                        </b-card-text>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>
        <reservation-send-email-modal
            modal-id="send-email-modal"
            :initial-values="values.reservation"
            :selected-report-template="selectedReportTemplate"
            :onUpdate="onModalUpdate"
        />
        <reservation-payment-modal
            modal-id="payment-modal"
            :initial-values="values.reservation"
            :type="billType"
            :mode="billMode"
            :onUpdate="onModalUpdate"
        />
        <modal
            id="change-airbnb-reservation-to-paid"
            title="Change Airbnb Reservation Status"
            @ok="changeAirbnbReservationToPaid"
            ok-title="Proceed"
            
        >
            You are about to change reservation status to Paid. Are you sure you want to proceed?
        </modal>
        <report-templates-modal
            :report-templates="reportTemplatesOriginal"
            :onUpdate="onUpdateReportTemplates"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import isNil from 'lodash/isNil';
import API from '../../../api';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import Modal from '../../generic/modal/modal.vue';
import DigitalSignatureRequestModal from '../../common/digital-signature-request-modal.vue';
import ReservationSendEmailModal from '../../common/reservation-send-email-modal.vue';
import ReservationPaymentModal from '../../common/reservation-payment-modal.vue';
import ReportTemplatesModal from './reservation-invoice-report-templates-modal.vue';
import ReservationHasServicesIcon from '../../common/reservation-has-services-icon.vue';
import ReservationDigitalSignatureIcon from '../../common/reservation-digital-signature-icon.vue';
import ReservationTravelInsuranceIcon from '../../common/reservation-travel-insurance-icon.vue';
import ReservationGuestStatusIcon from '../../common/reservation-guest-status-icon.vue';
import ReservationVerificationStatusIcon from '../../common/reservation-verification-status-icon.vue';

import ReservationPetsIcon from '../../common/reservation-pets-icon.vue';
import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
import { invoiceMixin } from '../mixins/invoice-mixin';
import ReservationInvoiceStateIcon from './reservation-invoice-state-icon.vue';
import {
    getReservationStatusColor,
    getReservationStatusIcon,
    getShowOtherDeposits,
    getOtherDepositsPaid,
    getBalance,
    getBalanceLabel,
} from './print/utils';
import {
    RESERVATION_INVOICE_REPORT_TEMPLATES_ENDPOINT,
    RESERVATION_INVOICE_REPORT_TEMPLATE_INLINE_ENDPOINT,
    RESERVATION_INVOICE_REPORT_TEMPLATE_DOWNLOAD_ENDPOINT,
    RESERVATION_INVOICE_REPORT_TEMPLATE_EDIT_ENDPOINT,
    RESERVATION_INVOICE_CHANGE_AIRBNB_RESERVATION_STATUS_ENDPOINT,
} from '../../../endpoints';
import { PAYMENT_METHOD_CONFIRMATION_STATUS_CONFIRMED } from '../../../constants';

export default {
    name: 'ReservationInvoiceSidebar',
    components: {
        Modal,
        BusyModal,
        DigitalSignatureRequestModal,
        ReportTemplatesModal,
        ReservationHasServicesIcon,
        ReservationTravelInsuranceIcon,
        ReservationDigitalSignatureIcon,
        ReservationGuestStatusIcon,
        ReservationPetsIcon,
        ReservationSendEmailModal,
        ReservationPaymentModal,
        ReservationInvoiceStateIcon,
        ReservationVerificationStatusIcon,
    },
    mixins: [invoiceMixin, removeScrollMixin],
    props: {
        initialValues: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        ...mapGetters('header', [
            'userToday',
            'userUnicodeDateFormat',
            'userHasPayPal',
            'userHasPreauth',
            'userVirtualTerminalUrl',
            'userPermissions',
            'userHasStripeGateway',
        ]),
        isQuote() {
            return this.values.reservationStatus.state === 'QUOTE';
        },
        isECheck() {
            return this.values.reservation.process_payment_data?.profile_type == 1 || false;
        },
        isAirBnbConfirmed() {
            return (
                this.values.reservationStatus.state === 'PAIDDEPOSIT' &&
                this.values.reservation.booking_method === 'AIRBNB'
            );
        },
        processPaymentDisabled() {
            return (
                !this.values.reservation.process_payment_data ||
                this.values.reservation.payment_method_confirmation_status !==
                    PAYMENT_METHOD_CONFIRMATION_STATUS_CONFIRMED ||
                this.values.reservation.is_process_payment_disabled
            );
        },
        processPreAuthDisabled() {
            return (
                !this.userHasPreauth ||
                !this.values.reservation.process_payment_data ||
                this.values.reservation.payment_method_confirmation_status !==
                    PAYMENT_METHOD_CONFIRMATION_STATUS_CONFIRMED ||
                (this.isECheck && this.userHasStripeGateway)
            );
        },
        balance() {
            return this.values && getBalance(this.values);
        },
        isCanManageDocumentShortcuts() {
            if (this.userPermissions !== undefined) {
                return (
                    this.userPermissions.can_manage_document_shortcuts &&
                    !this.values.reservation.airbnb_messaging_api &&
                    !this.values.reservation.vrbo_messaging_api
                );
            }
            return false;
        },
    },
    mounted() {
        this.fetchReportTemplates();
    },
    data() {
        return {
            loading: false,
            values: this.getInitialValues(),
            balanceClass: '',
            isRemaining: false,
            reportTemplates: [],
            reportTemplatesFiltered: [],
            reportTemplatesNoPayPal: [],
            reportTemplatesOriginal: [],
            selectedReportTemplate: null,
            selectedUnlayerTemplate: null,
            billType: 0,
            billMode: 'RECORD',
            emailMode: 'EMAIL',
        };
    },
    methods: {
        isNil,
        getInitialValues() {
            this.balanceClass = '';
            this.isRemaining = false;

            return {
                reservation: this.initialValues.reservationInvoice,
                reservationId: this.initialValues.reservationInvoice.id,
                reservationStatus: this.initialValues.reservationInvoice.reservation_status,
                reservationSchedule: this.initialValues.reservationInvoice.reservation_data.schedule,
                stayType: this.initialValues.reservationInvoice.stay_type,
                confirmationBreakdown: this.initialValues.reservationInvoice.reservation_data.confirmation_breakdown,
                hasTravelInsurance: this.initialValues.reservationInvoice.has_travel_insurance,
                hasPets: this.initialValues.reservationInvoice.has_pets,
            };
        },
        getBalanceLabel,
        getBalance,
        getOtherDepositsPaid,
        getShowOtherDeposits,
        onModalUpdate() {
            this.refreshReservationInvoice(this.values.reservationId);
        },
        onUpdateReportTemplates() {
            this.fetchReportTemplates();
        },
        async fetchReportTemplates() {
            try {
                const response = await API.get(RESERVATION_INVOICE_REPORT_TEMPLATES_ENDPOINT());
                this.reportTemplates = [
                    {
                        id: null,
                        report_name: 'Message',
                    },
                ];
                if (!this.values.reservation.airbnb_messaging_api && !this.values.reservation.vrbo_messaging_api) {
                    this.reportTemplates = this.reportTemplates.concat(response.data);
                    this.reportTemplatesOriginal = this.reportTemplates.filter((obj) => {
                        return obj.id !== null;
                    });
                    this.reportTemplatesFiltered = this.reportTemplates.filter((obj) => {
                        return obj.is_default_confirmation || obj.is_default_invoice || obj.id === null || obj.shortcut;
                    });
                    this.reportTemplatesNoPayPal = this.reportTemplates.filter((obj) => {
                        return !obj.is_paypal_invoice && obj.show && obj.id;
                    });
                } else {
                    this.reportTemplatesFiltered = this.reportTemplates;
                    this.reportTemplatesNoPayPal = this.reportTemplates.concat(response.data).filter((obj) => {
                        return !obj.is_paypal_invoice && obj.show && obj.id;
                    });
                }
            } catch (err) {
                this.errorToast({ text: 'Could not load report template.' });
            }
        },
        getReportTemplateInlineURL(id) {
            if (id != null) {
                return RESERVATION_INVOICE_REPORT_TEMPLATE_INLINE_ENDPOINT(this.values.reservationId, id);
            }
        },
        getReportTemplateDownloadURL(id) {
            return RESERVATION_INVOICE_REPORT_TEMPLATE_DOWNLOAD_ENDPOINT(this.values.reservationId, id);
        },
        getReportTemplateEditURL(id) {
            return RESERVATION_INVOICE_REPORT_TEMPLATE_EDIT_ENDPOINT(id);
        },
        showSendEmailModal(id) {
            this.emailMode = 'EMAIL';

            this.selectedReportTemplate = this.reportTemplates.find((item) => item.id === id);
            this.$bvModal.show('send-email-modal');
        },
        showSendPayPalInvoiceEmailModal(id) {
            this.emailMode = 'PAYPAL';
            this.selectedReportTemplate = this.reportTemplates.find(
                (item) => item.default_report_type_name === 'PayPal Invoice'
            );
            this.$bvModal.show('send-email-modal');
        },
        getRecordPaymentColumns() {
            if (!this.values.reservation.process_payment_data) {
                return 12;
            }
            return 6;
        },
        recordPayment() {
            this.billMode = 'RECORD';
            this.billType = 0;
            this.$bvModal.show('payment-modal');
        },
        recordRefund() {
            this.billMode = 'RECORD';
            this.billType = 1;
            this.$bvModal.show('payment-modal');
        },
        recordPreAuth() {
            this.billMode = 'RECORD';
            this.billType = 2;
            this.$bvModal.show('payment-modal');
        },
        processPayment() {
            this.billMode = 'PROCESS';
            this.billType = 0;
            this.$bvModal.show('payment-modal');
        },
        processRefund() {
            this.billMode = 'PROCESS';
            this.billType = 1;
            this.$bvModal.show('payment-modal');
        },
        processPreAuth() {
            this.billMode = 'PROCESS';
            this.billType = 2;
            this.$bvModal.show('payment-modal');
        },
        getReservationStatusIcon,
        getReservationStatusColor,
        showChangeAirbnbReservationToPaidModal() {
            if (this.isAirBnbConfirmed) {
                this.$bvModal.show('change-airbnb-reservation-to-paid');
            }
        },
        async changeAirbnbReservationToPaid() {
            try {
                const response = await API.post(
                    RESERVATION_INVOICE_CHANGE_AIRBNB_RESERVATION_STATUS_ENDPOINT(this.values.reservation.id)
                );
                this.refreshReservationInvoice(this.values.reservation.id);
                this.successToast({ text: `Reservation status successfully changed` });
            } catch (err) {
                this.errorToast({ text: 'Could not load change reservation status.' });
            }
        },
        showReportTemplatesModal() {
            if (
                !this.values.reservation.airbnb_messaging_api &&
                !this.values.reservation.vrbo_messaging_api &&
                this.userPermissions.can_manage_document_shortcuts
            ) {
                this.$bvModal.show('report-templates-modal');
            }
        },
    },
    watch: {
        initialValues() {
            this.values = this.getInitialValues();
        },
    },
};
</script>

<style lang="scss" scoped>
@import './reservation-invoice-sidebar.scss';
</style>
